import {
  Fragment,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { Dialog, Transition } from "@headlessui/react";
import IconCloseX from "~/components/icons/IconCloseX";
import { cn } from "./utils/cn";
import IconButton from "./IconButton";
import {
  clearModalOriginMotion,
  markTransactionOriginLand,
  playModalOriginMotion,
  prefersReducedMotion,
  type ModalOrigin,
  type OriginMotionHandle,
} from "./modalOriginMotion";

export type { ModalOrigin };

export type ModalSize = "sm" | "md" | "lg" | "xl" | "full";

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  size?: ModalSize;
  /** Hide the default close (X) button. */
  hideClose?: boolean;
  /** Larger, filled close control (about 1.5× the default icon button). */
  prominentClose?: boolean;
  /** Hide the mobile bottom-sheet grab handle (e.g. non-dismissible-by-drag first-run sheets). */
  hideHandle?: boolean;
  /**
   * On viewports below `sm`, cover the screen instead of opening as a bottom
   * sheet. Desktop (`sm+`) keeps the centered dialog.
   */
  mobileFullscreen?: boolean;
  /**
   * Center the panel on every viewport. Without this, viewports below `sm`
   * open the panel as a bottom sheet.
   */
  centered?: boolean;
  /** Header content rendered on one row with the close button. */
  title?: ReactNode;
  /**
   * Sticky footer actions (use ui Button). Rendered end-aligned above a divider.
   * Convention: secondary/ghost cancel first, primary action last.
   */
  footer?: ReactNode;
  panelClassName?: string;
  /** Extra classes for the scrollable body region. */
  bodyClassName?: string;
  /**
   * Change this value to scroll the body back to the top (e.g. pass the
   * current step of a multi-stage modal). The body also resets on open.
   */
  scrollResetKey?: unknown;
  /**
   * When set, the panel springs out of this rectangle on open and back into
   * it on close. `onExited` fires after that flight (or immediately when
   * motion is skipped).
   */
  origin?: ModalOrigin | null;
  onExited?: () => void;
  ["data-cy"]?: string;
}

// Fixed width per size — the panel always fills to this max width (never sizes
// to content), so a modal never shrinks/grows when its content changes (e.g.
// switching tabs).
const sizeClasses: Record<ModalSize, string> = {
  sm: "sm:max-w-md",
  md: "sm:max-w-xl",
  lg: "sm:max-w-3xl",
  xl: "sm:max-w-5xl",
  // Full-screen: fills the viewport on sm+ (mobile is already a near-full sheet).
  // `!` overrides the conflicting base panel utilities (sm:my-8, sm:max-h-[88vh],
  // sm:w-full, sm:rounded-modal) regardless of class order.
  full: "sm:!my-0 sm:!h-[100dvh] sm:!max-h-[100dvh] sm:!w-screen sm:!max-w-none sm:!rounded-none",
};

/**
 * Canonical modal. Headless UI Dialog with a fixed structure shared by every
 * modal in the app: mobile bottom-sheet, sticky header (title + close X on one
 * row, single canonical X size/placement), scrollable body, and an optional
 * sticky footer for actions. One backdrop, one width-per-size, one close.
 */
const Modal = ({
  open,
  onClose,
  children,
  size = "md",
  hideClose,
  hideHandle,
  prominentClose,
  mobileFullscreen,
  centered = false,
  title,
  footer,
  panelClassName,
  bodyClassName,
  scrollResetKey,
  origin,
  onExited,
  ...props
}: ModalProps) => {
  const showHeader = Boolean(title) || !hideClose;
  const bodyRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const onExitedRef = useRef(onExited);
  const exitedRef = useRef(false);
  const originRef = useRef<ModalOrigin | null>(origin ?? null);
  const openRef = useRef(open);
  const motionFlagRef = useRef(false);
  const enteredRef = useRef(false);
  const enterMotionRef = useRef<OriginMotionHandle | null>(null);
  onExitedRef.current = onExited;
  openRef.current = open;

  const originEligible = Boolean(
    origin && origin.width > 1 && origin.height > 1 && !prefersReducedMotion()
  );
  // Latch the flight for the whole open → close cycle. Dropping `origin` in
  // `onExited` must not swap this backdrop for Headless UI's leave transition
  // (that one starts at full opacity and flashes the scrim back in). The
  // dialog also hides itself when the flight ends, so a caller that never
  // clears `origin` does not leave scroll locked.
  const [originSession, setOriginSession] = useState(open && originEligible);
  const [sessionOrigin, setSessionOrigin] = useState<ModalOrigin | null>(
    open && originEligible ? origin ?? null : null
  );
  const [presented, setPresented] = useState(open);
  const [trackedOpen, setTrackedOpen] = useState(open);
  if (open !== trackedOpen) {
    setTrackedOpen(open);
    if (open) {
      setOriginSession(originEligible);
      setSessionOrigin(originEligible ? origin ?? null : null);
      setPresented(true);
    }
  }
  const show = originSession ? presented : open;
  originRef.current = originSession ? sessionOrigin : origin ?? null;
  motionFlagRef.current = originSession;
  const [backdropIn, setBackdropIn] = useState(false);

  // The dialog panel is portaled, so it may not exist during the first layout
  // effect. Start the flight from the ref callback, which runs in the commit
  // that actually inserts the panel — before the browser paints it full size.
  const setPanelRef = useCallback((node: HTMLDivElement | null) => {
    panelRef.current = node;
    if (!node) {
      enteredRef.current = false;
      return;
    }
    if (enteredRef.current) return;

    let attempts = 0;
    const start = () => {
      if (enteredRef.current || panelRef.current !== node) return;
      if (!openRef.current || !motionFlagRef.current || !originRef.current) {
        return;
      }
      const motion = playModalOriginMotion(node, originRef.current, "enter");
      if (!motion) {
        if (attempts < 5) {
          attempts += 1;
          requestAnimationFrame(start);
        }
        return;
      }
      enteredRef.current = true;
      enterMotionRef.current = motion;
      motion.finished.then(() => {
        if (enterMotionRef.current !== motion) return;
        clearModalOriginMotion(node, motion);
        enterMotionRef.current = null;
      });
    };
    start();
  }, []);

  // Open the body at the top, and return to it on stage changes. The second
  // reset on the next frame wins over Headless UI's initial focus, which can
  // scroll a bottom-of-body control into view right after mount.
  useEffect(() => {
    if (!open) return;
    const el = bodyRef.current;
    if (!el) return;
    el.scrollTop = 0;
    const raf = requestAnimationFrame(() => {
      el.scrollTop = 0;
    });
    return () => cancelAnimationFrame(raf);
  }, [open, scrollResetKey]);

  useEffect(() => {
    if (!originSession || !open) {
      setBackdropIn(false);
      return;
    }
    let secondFrame = 0;
    const firstFrame = requestAnimationFrame(() => {
      secondFrame = requestAnimationFrame(() => setBackdropIn(true));
    });
    return () => {
      cancelAnimationFrame(firstFrame);
      cancelAnimationFrame(secondFrame);
    };
  }, [open, originSession]);

  useLayoutEffect(() => {
    if (open) {
      exitedRef.current = false;
      return;
    }
    if (originSession || exitedRef.current) return;
    exitedRef.current = true;
    onExitedRef.current?.();
  }, [open, originSession]);

  useLayoutEffect(() => {
    if (open || !originSession || !sessionOrigin) return;
    const panel = panelRef.current;
    let timeout = 0;
    const finish = () => {
      if (exitedRef.current) return;
      exitedRef.current = true;
      window.clearTimeout(timeout);
      markTransactionOriginLand(sessionOrigin);
      setPresented(false);
      onExitedRef.current?.();
    };
    if (!panel) {
      finish();
      return;
    }
    if (enterMotionRef.current) {
      clearModalOriginMotion(panel, enterMotionRef.current);
      enterMotionRef.current = null;
    }
    let active = true;
    const motion = playModalOriginMotion(panel, sessionOrigin, "leave");
    if (!motion) {
      finish();
      return;
    }
    timeout = window.setTimeout(() => {
      if (!active) return;
      finish();
    }, 500);
    motion.finished.then(() => {
      if (!active) return;
      // Keep the closing transform until unmount. Cancelling it first would
      // snap the panel back to full size for a frame.
      finish();
    });
    return () => {
      active = false;
      window.clearTimeout(timeout);
      // A finished flight should keep its end transform until the panel
      // unmounts. Clearing it here snaps the dialog back to full size.
      if (exitedRef.current) return;
      clearModalOriginMotion(panel, motion);
    };
  }, [open, originSession, sessionOrigin]);

  return (
    <Transition.Root show={show} as={Fragment}>
      <Dialog as="div" className="relative z-modal" onClose={onClose}>
        {originSession ? (
          <div
            className={cn(
              "fixed inset-0 bg-menu/30 backdrop-blur-md transition-opacity",
              backdropIn && open
                ? "opacity-100 duration-300"
                : "opacity-0 duration-200"
            )}
          />
        ) : (
          <Transition.Child
            as={Fragment}
            enter="ease-out-soft duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="transition-opacity fixed inset-0 bg-menu/30 backdrop-blur-md" />
          </Transition.Child>
        )}

        <div
          className={cn(
            "fixed inset-0 z-10 overflow-y-auto",
            mobileFullscreen && "modal-mobile-fullscreen-root"
          )}
        >
          <div
            className={cn(
              "flex min-h-full justify-center text-center",
              centered
                ? "items-center p-4 sm:p-0"
                : "items-end p-0 sm:items-center sm:p-0"
            )}
          >
            {/*
              Entrance uses a pure CSS animation (fill-mode `both`) instead of a
              Headless JS transition: heavy modal bodies re-render during the
              enter and could leave a Fragment-based Transition stuck in its
              `enterFrom` (opacity-0) state. A CSS keyframe always settles.
            */}
            {/*
              On a phone the fullscreen frame is position:fixed and never
              transformed. The origin flight transforms the panel inside it,
              so iOS keeps sizing the frame against the visible viewport.
            */}
            <div
              className={cn(
                "contents",
                mobileFullscreen && "modal-mobile-fullscreen"
              )}
            >
            <Dialog.Panel
              ref={setPanelRef}
              data-cy={props["data-cy"]}
              // Inline fill so twMerge can't drop the background when
              // panelClassName includes `bg-gradient-*` (those conflict with
              // `bg-surface` and would leave the panel transparent).
              style={{ backgroundColor: "rgb(var(--surface-base))" }}
              className={cn(
                "relative flex w-full flex-col overflow-hidden bg-surface text-start text-menu shadow-e4",
                !originSession && "transform",
                // Transform-only entrance; never animate opacity so a
                // re-rendering body can't leave the panel stuck invisible.
                // Origin flights replace this and clear their transform on end.
                centered
                  ? "max-h-[calc(100dvh-2rem)] rounded-modal sm:my-8 sm:max-h-[88vh]"
                  : "max-h-[92vh] rounded-t-modal sm:my-8 sm:max-h-[88vh] sm:w-full sm:rounded-modal",
                open &&
                  !originSession &&
                  (centered
                    ? "animate-scale-in"
                    : "animate-sheet-up sm:animate-scale-in"),
                sizeClasses[size],
                mobileFullscreen && "modal-mobile-fullscreen-panel",
                panelClassName
              )}
            >
              {/* Grab handle — mobile bottom-sheet only */}
              {!hideHandle && !centered && !mobileFullscreen && (
                <div
                  data-origin-fade=""
                  className="mx-auto mb-1 mt-3 h-1.5 w-10 shrink-0 rounded-pill bg-gray-300 sm:hidden"
                />
              )}

              {showHeader && (
                <div
                  data-origin-fade=""
                  className={cn(
                    "flex shrink-0 justify-between gap-3 px-5 pt-4 sm:px-8 sm:pt-6",
                    prominentClose ? "items-center" : "items-start",
                    mobileFullscreen && "modal-mobile-fullscreen-header"
                  )}
                >
                  {title ? (
                    <Dialog.Title className="text-lg font-semibold text-menu">
                      {title}
                    </Dialog.Title>
                  ) : (
                    <span aria-hidden="true" />
                  )}
                  {!hideClose && (
                    <IconButton
                      aria-label="Close"
                      size={prominentClose ? "md" : "sm"}
                      variant={prominentClose ? "soft" : "ghost"}
                      icon={
                        <IconCloseX
                          width={prominentClose ? 21 : 14}
                          height={prominentClose ? 21 : 14}
                        />
                      }
                      onClick={onClose}
                      className={
                        prominentClose
                          ? "h-9 w-9 border border-secondary/35 bg-surface shadow-e1"
                          : "-me-1 -mt-1 shrink-0"
                      }
                    />
                  )}
                </div>
              )}

              <div
                ref={bodyRef}
                data-origin-fade=""
                className={cn(
                  "min-h-0 flex-1 overflow-y-auto px-5 pt-3 sm:px-8",
                  // The last region before the viewport edge absorbs the
                  // Android-app bottom buffer (0px on web/iOS).
                  footer
                    ? "pb-6"
                    : "pb-[calc(1.5rem+var(--app-bottom-buffer,0px))]",
                  !showHeader && "pt-5",
                  bodyClassName
                )}
              >
                {children}
              </div>

              {footer && (
                <div
                  data-origin-fade=""
                  className={cn(
                    "flex shrink-0 flex-wrap items-center justify-end gap-2 border-t border-border-subtle px-5 pb-[calc(0.875rem+var(--app-bottom-buffer,0px))] pt-3.5 sm:px-8",
                    mobileFullscreen && "modal-mobile-fullscreen-footer"
                  )}
                >
                  {footer}
                </div>
              )}
            </Dialog.Panel>
            </div>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default Modal;
